import type { Map, MapGeoJSONFeature, PointLike } from "maplibre-gl";
import { tick } from "svelte";
import type { LngLat } from "../../../lib/geo/types.ts";
import type { LocatedOsmElement } from "../../../lib/osm.ts";
import { attachMapSelectionGestures } from "../map/mapSelectionGestures.ts";
import { attachMapHoverPresentation } from "../map/hover.ts";
import { attachMapSelectedFeaturePresentation } from "../map/selectedFeature.ts";
import {
  createOsmSelectionLoader,
  type OsmSelectionTarget,
  type OsmState,
} from "./osmLookup.ts";

interface MapSelectionOptions {
  map: () => Map | undefined;
  occupiedDesktopWidth: () => number;
  contextMenuTrigger: () => HTMLElement | null;
  // Map-driven lookups may need to clear transient UI state that belongs only
  // to other interaction sources before starting a new selection.
  onMapSelectionStart?: () => void;
}

export function useMapSelection(
  options: MapSelectionOptions,
): {
  get osm(): OsmState;
  get contextMenuLngLat(): LngLat | undefined;
  get canLookupContextMenuTarget(): boolean;
  selectFromMapFeature: (
    selectionTarget: OsmSelectionTarget,
    renderedFeature?: MapGeoJSONFeature,
  ) => Promise<void>;
  // Call this for interactions that did not originate from a rendered map
  // feature, such as POI result markers, geocoder results, or context-menu
  // coordinate lookups. Those flows should clear any stale rendered overlay.
  selectFromExternalTarget: (
    selectionTarget: OsmSelectionTarget,
  ) => Promise<void>;
  selectOsmElement: (next: LocatedOsmElement) => void;
  clearSelection: () => void;
  showContextMenuLookup: () => void;
} {
  let osm = $state<OsmState>({ status: "idle" });
  // Storing the coordinates associated with a context menu click between that click and the user selecting "What's here?" from the context menu.
  let contextMenuLngLat = $state<LngLat | undefined>(undefined);
  let selectedFeaturePresentation:
    | ReturnType<typeof attachMapSelectedFeaturePresentation>
    | undefined;

  function setState(next: OsmState): void {
    osm = next;
  }

  const osmLoader = createOsmSelectionLoader((next) => {
    setState(next);
  });

  function measureFocusOffset(): PointLike | undefined {
    const occupiedDesktopWidth = options.occupiedDesktopWidth();
    if (occupiedDesktopWidth === 0) {
      return undefined;
    }
    return [Math.ceil(occupiedDesktopWidth / 2), 0];
  }

  async function selectTarget(
    selectionTarget: OsmSelectionTarget,
  ): Promise<void> {
    osmLoader.load(selectionTarget);
    // The side panel's measured width is part of the camera offset. Waiting for
    // the loading panel to render avoids duplicating layout constants here.
    await tick();
    selectedFeaturePresentation?.focusLngLat(
      selectionTarget.lngLat,
      measureFocusOffset(),
    );
  }

  function selectFromExternalTarget(
    selectionTarget: OsmSelectionTarget,
  ): Promise<void> {
    selectedFeaturePresentation?.clearRenderedSelection();
    return selectTarget(selectionTarget);
  }

  // Rendered-feature selections own the temporary overlay that keeps the
  // clicked map feature visibly selected. External selections must not leave
  // an old overlay behind, so they go through selectFromExternalTarget().
  function selectFromMapFeature(
    selectionTarget: OsmSelectionTarget,
    renderedFeature?: MapGeoJSONFeature,
  ): Promise<void> {
    options.onMapSelectionStart?.();
    if (renderedFeature) {
      selectedFeaturePresentation?.showRenderedSelection(renderedFeature);
    } else {
      selectedFeaturePresentation?.clearRenderedSelection();
    }
    return selectTarget(selectionTarget);
  }

  function selectOsmElement(next: LocatedOsmElement): void {
    if (osm.status !== "success") return;
    setState({ ...osm, active: next });
    selectedFeaturePresentation?.focusLngLat(next.lnglat, measureFocusOffset());
  }

  function clearSelectionState(): void {
    osmLoader.cancel();
    setState({ status: "idle" });
  }

  function clearSelection(): void {
    selectedFeaturePresentation?.clearSelection();
    clearSelectionState();
  }

  function showContextMenuLookup(): void {
    if (!contextMenuLngLat) return;
    options.onMapSelectionStart?.();
    void selectFromExternalTarget({
      kind: "nearby",
      lngLat: contextMenuLngLat,
      nameHint: undefined,
    });
  }

  $effect(() => {
    const map = options.map();
    if (!map) return;

    const hoverPresentation = attachMapHoverPresentation(map);
    const featurePresentation = attachMapSelectedFeaturePresentation(map);
    selectedFeaturePresentation = featurePresentation;

    const gestures = attachMapSelectionGestures(map, {
      onSelect: (selectionTarget, renderedFeature) => {
        if (osm.status !== "idle") {
          // Consume the map click that dismisses the panel so it cannot also
          // select a feature underneath and immediately reopen another panel.
          clearSelection();
          return;
        }
        void selectFromMapFeature(selectionTarget, renderedFeature);
      },
      onClear: () => {
        featurePresentation.clearSelection();
        clearSelectionState();
      },
      onContextMenuTarget: (lngLat) => {
        contextMenuLngLat = lngLat;
      },
      contextMenuTrigger: options.contextMenuTrigger(),
    });

    return () => {
      gestures.destroy();
      featurePresentation.destroy();
      hoverPresentation.destroy();
      if (selectedFeaturePresentation === featurePresentation) {
        selectedFeaturePresentation = undefined;
      }
    };
  });

  return {
    get osm() {
      return osm;
    },
    get contextMenuLngLat() {
      return contextMenuLngLat;
    },
    get canLookupContextMenuTarget() {
      return contextMenuLngLat !== undefined;
    },
    selectFromMapFeature,
    selectFromExternalTarget,
    selectOsmElement,
    clearSelection,
    showContextMenuLookup,
  };
}
